/* Copy do Painel (ADR-0700): título/descrição do header, o CTA "Ver métricas", os cartões de KPI
   (MRR, orgs, signups, usuários ativos, churn, fila de moderação) e os atalhos de acesso rápido. */
import type { Bilingual } from '../bilingual.js';
import { useBilingual } from '../locale-provider.js';

interface DashboardContent {
  readonly title: string;
  readonly description: string;
  readonly viewMetrics: string;
  readonly loadFailed: string;
  readonly quickAccess: string;
  readonly stats: {
    readonly mrr: string;
    readonly organizations: string;
    /** "N bloqueadas" — hint sob o total de organizações. `formatted` já vem de `fmtNumber`. */
    readonly blockedOrgs: (formatted: string) => string;
    readonly signups30d: string;
    /** "N nos últimos 7d" — hint sob signups de 30 dias. */
    readonly signups7d: (formatted: string) => string;
    readonly activeUsers24h: string;
    /** "N devices" — hint sob usuários ativos. */
    readonly activeDevices: (formatted: string) => string;
    readonly churn30d: string;
    readonly moderationQueue: string;
    /** "N acima do SLA" — hint sob a fila de moderação. */
    readonly aboveSla: (formatted: string) => string;
  };
  readonly shortcuts: {
    readonly companiesLabel: string;
    readonly companiesDescription: string;
    readonly publishersLabel: string;
    readonly publishersDescription: string;
    readonly moderationLabel: string;
    readonly moderationDescription: string;
    readonly supportLabel: string;
    readonly supportDescription: string;
    readonly metricsLabel: string;
    readonly metricsDescription: string;
  };
}

const DASHBOARD_CONTENT: Bilingual<DashboardContent> = {
  'pt-BR': {
    title: 'Painel',
    description: 'Visão geral do ecossistema Planuze.',
    viewMetrics: 'Ver métricas',
    loadFailed: 'Falha ao carregar os indicadores.',
    quickAccess: 'Acesso rápido',
    stats: {
      mrr: 'MRR (USD)',
      organizations: 'Organizações',
      blockedOrgs: (formatted) => `${formatted} bloqueadas`,
      signups30d: 'Signups (30d)',
      signups7d: (formatted) => `${formatted} nos últimos 7d`,
      activeUsers24h: 'Usuários ativos (24h)',
      activeDevices: (formatted) => `${formatted} devices`,
      churn30d: 'Churn (30d)',
      moderationQueue: 'Fila de moderação',
      aboveSla: (formatted) => `${formatted} acima do SLA`,
    },
    shortcuts: {
      companiesLabel: 'Empresas',
      companiesDescription: 'Governança, planos e bloqueios',
      publishersLabel: 'Publishers',
      publishersDescription: 'Verificação e status',
      moderationLabel: 'Moderação',
      moderationDescription: 'Fila de submissões de packs',
      supportLabel: 'Suporte',
      supportDescription: 'Tickets dos usuários',
      metricsLabel: 'Métricas',
      metricsDescription: 'Análise detalhada e séries',
    },
  },
  'en-US': {
    title: 'Dashboard',
    description: 'Overview of the Planuze ecosystem.',
    viewMetrics: 'View metrics',
    loadFailed: 'Failed to load the indicators.',
    quickAccess: 'Quick access',
    stats: {
      mrr: 'MRR (USD)',
      organizations: 'Organizations',
      blockedOrgs: (formatted) => `${formatted} blocked`,
      signups30d: 'Signups (30d)',
      signups7d: (formatted) => `${formatted} in the last 7d`,
      activeUsers24h: 'Active users (24h)',
      activeDevices: (formatted) => `${formatted} devices`,
      churn30d: 'Churn (30d)',
      moderationQueue: 'Moderation queue',
      aboveSla: (formatted) => `${formatted} above SLA`,
    },
    shortcuts: {
      companiesLabel: 'Companies',
      companiesDescription: 'Governance, plans, and blocks',
      publishersLabel: 'Publishers',
      publishersDescription: 'Verification and status',
      moderationLabel: 'Moderation',
      moderationDescription: 'Pack submission queue',
      supportLabel: 'Support',
      supportDescription: 'User tickets',
      metricsLabel: 'Metrics',
      metricsDescription: 'Detailed analysis and series',
    },
  },
};

export const useDashboardContent = (): DashboardContent => useBilingual(DASHBOARD_CONTENT);
